import React, { useMemo } from 'react';

/**
 * Horizontal strip above the plot (uses chart top margin) with one circle per Strava "Race" activity.
 * x positions use the same scale as the main X axis (`xDataKey` on each race, default `date`, or `chartX` when mapped).
 */
export function AcwrRaceStripLayer({ xAxisMap, offset, races, visible, onHover, onLeave, xDataKey = 'date' }) {
  const placed = useMemo(() => {
    if (!races?.length) return [];
    const byX = new Map();
    for (const r of races) {
      const xv = r[xDataKey];
      if (xv == null || xv === '') continue;
      const key = String(xv);
      if (!byX.has(key)) byX.set(key, []);
      byX.get(key).push(r);
    }
    const out = [];
    for (const [, list] of byX) {
      const n = list.length;
      list.forEach((race, i) => {
        const jitter = n === 1 ? 0 : (i - (n - 1) / 2) * 7;
        out.push({ race, jitter, xCat: String(race[xDataKey]) });
      });
    }
    return out.sort((a, b) => String(a.race.date).localeCompare(String(b.race.date)));
  }, [races, xDataKey]);

  if (!visible || !placed.length || !offset) return null;

  const xAxis = xAxisMap?.[0] ?? (xAxisMap ? Object.values(xAxisMap)[0] : null);
  if (!xAxis?.scale) return null;

  const scale = xAxis.scale;
  const stripY = offset.top - 14;
  if (!Number.isFinite(stripY) || stripY < 0) return null;

  const x0 = offset.left;
  const x1 = offset.left + offset.width;

  return (
    <g className='recharts-layer acwr-race-strip-layer' pointerEvents='auto'>
      <line
        x1={x0}
        y1={stripY}
        x2={x1}
        y2={stripY}
        stroke='hsl(var(--border))'
        strokeWidth={1}
        strokeOpacity={0.85}
        pointerEvents='none'
      />
      {placed.map(({ race, jitter, xCat }) => {
        const raw = scale(xCat);
        if (!Number.isFinite(raw)) return null;
        const bw = typeof scale.bandwidth === 'function' ? scale.bandwidth() : 0;
        const cx = raw + (bw > 0 ? bw / 2 : 0) + jitter;
        const cy = stripY;
        return (
          <circle
            key={`${race.id}-${xCat}`}
            cx={cx}
            cy={cy}
            r={5}
            fill='hsl(var(--primary))'
            stroke='hsl(var(--card))'
            strokeWidth={1.5}
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => {
              onHover?.({
                race,
                clientX: e.clientX,
                clientY: e.clientY,
              });
            }}
            onMouseLeave={() => onLeave?.()}
            onFocus={(e) => {
              onHover?.({
                race,
                clientX: e.clientX,
                clientY: e.clientY,
              });
            }}
            onBlur={() => onLeave?.()}
            tabIndex={0}
          />
        );
      })}
    </g>
  );
}
